import { t } from '@lingui/core/macro';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';

import { type AudienceOptionType } from '@/api/audience';
import { AudienceSelect } from '@/features/inputs';
import { cn } from '@/lib/utils';

import { usePageFilterContext } from './PageFilterProvider';
import useResolvedAudience from './useResolvedAudience';

type Props = {
  className?: string;
  defaultAudience?: AudienceOptionType;
  isLoading?: boolean;
  disabled?: boolean;
  prependedOptions?: AudienceOptionType[];
};

const PageAudienceFilter = ({
  className = '',
  defaultAudience,
  isLoading: isLoadingProp = false,
  disabled = false,
  prependedOptions,
}: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPageFilters } = usePageFilterContext();
  const audienceIdParamValue = searchParams.get('audience') ?? undefined;

  const {
    audience: currentAudience,
    isLoading,
    error,
  } = useResolvedAudience({
    urlAudienceId: audienceIdParamValue,
    defaultAudience,
    prependedOptions,
  });

  useEffect(() => {
    setPageFilters({ audience: currentAudience });
  }, [currentAudience, setPageFilters]);

  const handleAudienceChange = (selectedAudience: AudienceOptionType | null) => {
    if (
      (defaultAudience && defaultAudience?.id === selectedAudience?.id) ||
      (!defaultAudience && selectedAudience?.isTAM)
    ) {
      searchParams.delete('audience');
    } else if (selectedAudience?.id) {
      searchParams.set('audience', selectedAudience.id);
    }

    setSearchParams(searchParams);
  };

  return (
    <AudienceSelect
      className={cn('min-w-75', className)}
      value={currentAudience}
      placeholder={t`+ Add Audience`}
      disabled={disabled}
      isLoading={isLoadingProp || isLoading}
      error={error}
      showSizeInOption
      prependedOptions={prependedOptions}
      onChange={handleAudienceChange}
    />
  );
};

export default PageAudienceFilter;
